import { CheckCircle2 } from 'lucide-react';
import * as React from 'react';

import type { CguPdfRef } from '@/api/legal-client';
import { useCurrentCguPdf } from '@/api/use-legal';
import { PdfDocument } from '@/routes/cgu/pdf-document';
import { useScrollToEnd } from '@/routes/cgu/use-scroll-to-end';

// =============================================================================
// document-viewer.tsx — Abstraction de rendu de document AGNOSTIQUE du renderer.
// =============================================================================
// G4 (additif → option 2) : le scroll-gate est posé en ABSTRACTION. `DocumentViewer`
// rend un document scrollable et ÉMET `onFullyScrolled` (et expose `hasReadToEnd`)
// quand l'utilisateur a parcouru l'INTÉGRALITÉ du document. Le flux d'acceptation
// (cgu-screen) gate le bouton « accepter » UNIQUEMENT sur ce signal — il ne connaît
// RIEN du renderer interne.
//
// S7 (rendu réel) : le CORPS interne rend les OCTETS RÉELS du PDF via pdf.js (PdfDocument),
// résolus depuis le client legal (endpoint GET bytes). La détection « parcouru jusqu'au bout »
// reste posée par `useScrollToEnd` (renderer-agnostique, sur scrollTop/scrollHeight de la
// région scrollable) — discipline G4 : la détection N'EST PAS couplée au DOM pdf.js. Le flux
// d'acceptation reste INCHANGÉ : il ne dépend QUE de `onFullyScrolled` (props inchangées).
//
// Octets : fixture locale en stub (StubLegalClient), GCS write-once en prod (cut-over) —
// derrière la MÊME interface `currentCguPdf`. Le `pdfRef.sha256` (write-once) reste l'ancrage
// probatoire affiché ; il sert aussi de clé de cache stable des octets (immuables par version).
//
// a11y (WCAG 2.1 AA) : zone focusable au clavier (tabIndex=0 + role="region" labellisé) pour
// défiler sans souris ; état « parcouru » doublé d'un libellé texte (jamais couleur seule). La
// couche texte pdf.js porte le contenu lisible (sélection + lecteur d'écran).

export interface DocumentViewerProps {
  /** Référence du PDF affiché (bucket/objectKey/sha256) ; sha256 = ancrage probatoire + clé cache. */
  readonly pdfRef: CguPdfRef;
  /** Libellé accessible de la zone de lecture. */
  readonly label: string;
  /**
   * Émis UNE FOIS quand l'utilisateur a parcouru l'intégralité du document. Le flux
   * d'acceptation gate sur ce signal (et UNIQUEMENT sur lui).
   */
  readonly onFullyScrolled: () => void;
}

export function DocumentViewer({
  pdfRef,
  label,
  onFullyScrolled,
}: DocumentViewerProps): React.JSX.Element {
  const { ref, reachedEnd } = useScrollToEnd();
  const [hasReadToEnd, setHasReadToEnd] = React.useState(false);
  // Octets du PDF (cache stable sur le sha256 write-once : immuable par version).
  const pdf = useCurrentCguPdf(pdfRef.sha256);

  // Émet `onFullyScrolled` une seule fois (latch) : le flux d'acceptation reçoit un
  // signal stable, indépendant de l'implémentation interne du viewer.
  React.useEffect(() => {
    if (reachedEnd && !hasReadToEnd) {
      setHasReadToEnd(true);
      onFullyScrolled();
    }
  }, [reachedEnd, hasReadToEnd, onFullyScrolled]);

  return (
    <div className="space-y-3">
      <div
        ref={ref}
        role="region"
        aria-label={label}
        tabIndex={0}
        className="max-h-80 overflow-y-auto rounded-md border border-border bg-muted/20 p-4 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {/* Corps : rendu pdf.js des octets réels. La détection de scroll (useScrollToEnd) reste
            sur la région scrollable parente — découplée du DOM pdf.js (G4). */}
        {pdf.isPending ? (
          <p role="status" className="text-sm text-muted-foreground">
            Chargement du document…
          </p>
        ) : null}
        {pdf.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Le document n’a pas pu être chargé : {pdf.error.message}
          </p>
        ) : null}
        {pdf.isSuccess ? <PdfDocument data={pdf.data} /> : null}
        {/* Référence documentaire (probatoire) : numéro d'objet + empreinte sha256. */}
        <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
          Document : {pdfRef.objectKey} · empreinte sha256 :{' '}
          <span className="font-mono break-all">{pdfRef.sha256}</span>
        </p>
      </div>

      {/* État de parcours : doublé d'un libellé texte (jamais couleur seule). */}
      <p
        role="status"
        className={
          hasReadToEnd
            ? 'flex items-center gap-2 text-sm font-medium text-success'
            : 'flex items-center gap-2 text-sm text-muted-foreground'
        }
      >
        {hasReadToEnd ? <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
        {hasReadToEnd
          ? 'Lecture terminée. Vous pouvez signer.'
          : 'Faites défiler jusqu’en bas pour activer la signature.'}
      </p>
    </div>
  );
}
